import React, {useEffect, useMemo, useState} from "react"
import {connect} from "react-redux"
import {Link} from "react-router-dom"
import {Avatar, ButtonGroup, IconButton, Tooltip, useDisclosure,} from "@chakra-ui/react"
import {ColumnDef} from "@tanstack/react-table"
import {PencilIcon, TrashIcon} from "@heroicons/react/24/outline"


import {Card, CatIcon, Loading, Pagination, SearchInput, Table} from "@components/index"
import {DeleteModal} from "@components/overlay/DeleteModal"
import {ILabelSet} from "@backend/types"
import {useFilter} from "@hooks/index"
import {IconNameExt} from "@utils/icons"

import {labelSetActions, labelSetApiCalls} from "../../service"


function LabelSetList({labelSetReducer: {labelSets}, list, clear, labelSetDelete}) {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [itemId, setItemId] = useState<string | null>(null)
    const {filter, onFilterChange, setFilter} = useFilter<ILabelSet>(labelSetApiCalls.apiCalls)

    const columns = useMemo<ColumnDef<ILabelSet>[]>(
        () => [
            {
                header: "Label Set",
                accessorKey: "title",
                enableColumnFilter: false,
                cell: ({row: {original}}) => <div className="flex items-center">
                    <Avatar
                        bg={original.color}
                        className="mr-4"
                        icon={<CatIcon icon={original.icon as IconNameExt || "blender"} iconSize="lg"/>}/>
                    <span>{original.title}</span>
                </div>
            },
            {
                header: "Labels",
                accessorKey: "stats.labelCount",
                enableColumnFilter: false
            },
            {
                header: "Games",
                accessorKey: "stats.gameCount",
                enableColumnFilter: false
            },
            {
                header: "Actions",
                id: "actions",
                cell: ({row: {original}}) => <div>
                    <ButtonGroup variant="ghost" spacing="4" size="sm">
                        <Tooltip label="Edit" placement="top" openDelay={300}>
                            <IconButton
                                as={Link}
                                to={original._id.toString()}
                                aria-label="Edit Label"
                                icon={<PencilIcon className="w-4 h-4"/>}
                                colorScheme="blue">Edit</IconButton>
                        </Tooltip>
                        <Tooltip label="Delete" placement="top" openDelay={300}>
                            <IconButton
                                aria-label="Delete Label"
                                onClick={() => {
                                    setItemId(original._id.toString())
                                    onOpen()
                                }}
                                icon={<TrashIcon className="w-4 h-4"/>}
                                colorScheme="red">Delete</IconButton>
                        </Tooltip>
                    </ButtonGroup>
                </div>
            }
        ], [])

    useEffect(() => {
        if (!labelSets)
            list()
    }, [])

    function onLabelSetDelete() {

        labelSetDelete(itemId)
            .then()
            .finally(onClose)
    }

    if (!labelSets)
        return <Loading/>

    return <Card
        btnProps={{
            text: "New Label Set"
        }}
        title="Label Sets"
        search={<SearchInput
            placeholder="Search by label set title"
            onSearch={labelSetApiCalls.apiCalls.listWithPager}
            cb={setFilter}
        />}
        bodyClassName="p-0"
        footerClassName="flex justify-end items-center"
        footer={(filter?.results || labelSets).totalPages > 1 &&
          <Pagination
            page={filter?.query?.page || 0}
            pageCount={(filter?.results || labelSets).totalPages}
            onPageChange={onFilterChange}
          />}
    >
        <Table
            isLoading={filter?.isLoading}
            columns={columns}
            onFilterChange={onFilterChange}
            emptyState={{
                title: "No Label Set Created Yet",
                description: "You can create label sets by clicking the 'New Label Set' button."
            }}
            sort={[{
                id: "createdAt",
                desc: true
            }]}
            data={(filter?.results || labelSets || {}).docs}
        />
        <DeleteModal
            title="Delete Label Set?"
            message="Deleting a label set will not affect ongoing games. You can always restore it from the archived label sets panel."
            isOpen={isOpen}
            onCancel={onClose}
            onDelete={onLabelSetDelete}
        />
    </Card>
}

export default connect(({labelSetReducer}) => ({labelSetReducer}), {
    list: labelSetActions.actions.listWithPager,
    clear: labelSetActions.actions.clear,
    labelSetDelete: labelSetActions.actions.delete
})(LabelSetList)